/**
 * CurrentTimeIndicator Component
 * Red horizontal line indicating the current time on the calendar.
 * Updates every minute via useCurrentTime hook.
 * Supports campaign timezone for correct positioning when the campaign
 * operates in a different timezone than the browser.
 * Migrated to Tailwind CSS.
 */

import { format } from 'date-fns';
import { useCurrentTime } from '@/hooks';
import {
  calculateTimePosition,
  calculateTimePositionFromParts,
  getCurrentTimePartsInTimezone,
  HOUR_HEIGHT,
} from '@/lib/calendarUtils';
import { cn } from '@/lib/utils';

/** Size of the circle indicator on the left edge in pixels */
const CIRCLE_SIZE = 12;

interface CurrentTimeIndicatorProps {
  /** Whether this is today's column - shows more pronounced styling */
  isToday?: boolean;
  /** Whether to show the time label (only on first column) */
  showTimeLabel?: boolean;
  /** IANA timezone ID (e.g., "America/New_York"). Falls back to browser-local if undefined. */
  timeZoneId?: string;
}

/**
 * Format hours and minutes into a 12-hour time label (e.g., "2:30 pm").
 */
function formatTimeLabelFromParts(hours: number, minutes: number): string {
  const period = hours >= 12 ? 'pm' : 'am';
  const hours12 = hours % 12 || 12;
  const minutesStr = minutes.toString().padStart(2, '0');
  return `${hours12}:${minutesStr} ${period}`;
}

/**
 * Current time indicator with red line and optional circle.
 * - Today: Bold red line with circle indicator and time label
 * - Other days: Dimmer red line, no circle
 *
 * When timeZoneId is provided, positioning and the time label reflect the
 * campaign timezone rather than the browser's local time.
 */
export function CurrentTimeIndicator({
  isToday = false,
  showTimeLabel = false,
  timeZoneId,
}: CurrentTimeIndicatorProps) {
  // useCurrentTime ticks every minute to trigger re-renders
  const currentTime = useCurrentTime();

  let topPosition: number;
  let timeLabel: string;

  if (timeZoneId) {
    // Use timezone-aware parts for positioning and label
    const { hours, minutes } = getCurrentTimePartsInTimezone(timeZoneId);
    topPosition = calculateTimePositionFromParts(hours, minutes, HOUR_HEIGHT);
    timeLabel = formatTimeLabelFromParts(hours, minutes);
  } else {
    // Fallback: use browser-local time
    topPosition = calculateTimePosition(currentTime, HOUR_HEIGHT);
    timeLabel = format(currentTime, 'h:mm a').toLowerCase();
  }

  return (
    <div
      className={cn(
        "absolute left-0 right-0 h-0.5 z-10 pointer-events-none",
        isToday ? "bg-red-500" : "bg-red-300"
      )}
      style={{ top: topPosition }}
    >
      {/* Time label - positioned in the gutter area */}
      {showTimeLabel && (
        <span
          className="absolute text-[10px] font-medium text-red-500 whitespace-nowrap"
          style={{
            right: '100%',
            top: '-6px',
            paddingRight: '4px',
          }}
        >
          {timeLabel}
        </span>
      )}
      {/* Circle indicator - only shown on today */}
      {isToday && (
        <div
          className="absolute rounded-full bg-red-500"
          style={{
            width: CIRCLE_SIZE,
            height: CIRCLE_SIZE,
            left: -CIRCLE_SIZE / 2,
            top: -CIRCLE_SIZE / 2 + 1, // +1 to center on the 2px line
          }}
        />
      )}
    </div>
  );
}

export default CurrentTimeIndicator;
